import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { CustomContainer, connect } from "../../atoms/DependencyHelper";
import { setPaymentMethodUrl } from "../../../Redux/actions/checkoutAction";
import FormHeading from "../../atoms/checkoutForms/formHeading/FormHeading";
import PaymentMethodTile from "../../atoms/paymentMethodTile/PaymentMethodTile";
import ConfirmModal from "../../atoms/confirmModal/ConfirmModal";
import SnackBar from "../../atoms/snackBar/SnackBar";
import Image from "../../atoms/image/image";
import {
  showSpinner,
  hideSpinner,
  imagePrefix,
} from "../../../utils/helperServices";
import paymentServices from "../../../utils/paymentIntegrationServices";
import * as Analytics from "../../../services/analyticsUtag";
import { journeyType } from "../../../context/checkout-journey-context";
import config from "../../../config/index";
import labels from "../../../config/labels";
import styles from "./CheckoutPayment.module.scss";

const visaLabel = "VisaMasterCard";

const defaultPaymentTypes = [
  {
    name: visaLabel,
  },
];

const CheckoutPayment = forwardRef(
  (
    {
      setPaymentMethodUrl,
      continueDisable,
      userLoggedIn,
      profileData,
      navigate,
      paymentConfiguration,
      customerStatus,
      applicationType,
      currentJourneyType,
      cartDetails,
      deliveryDetails,
      authDetailsData,
      postOrderResponse,
    },
    ref
  ) => {
    const env = config.env;
    const [viewAnalytics, setViewAnalytics] = useState(false);
    const [intentData, setIntentData] = useState({});

    const paymentCallbackUrl =
      applicationType == "normal"
        ? config.envConfigs[env].paymentCallback
        : config.envConfigs[env].postPaymentCallback;

    const [paymentOptions, setPaymentOptions] = useState([]);
    const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);

    const [allCreditCards, setAllCreditCards] = useState([]);
    const [addCreditCard, setAddCreditCard] = useState(null);
    const [addCreditCardUrl, setAddCreditCardUrl] = useState(null);
    const [selectedCreditCard, setSelectedCreditCard] = useState(null);
    const [showAllCreditCards, setShowAllCreditCards] = useState(false);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const [showSnackBar, setShowSnackBar] = useState(false);
    const [snackBarMsg, setSnackBarMsg] = useState(null);
    const [showWarningMsg, setShowWarningMsg] = useState(false);

    const analyticsData = Analytics.getUtagData();

    useImperativeHandle(ref, () => ({
      submit: () => {
        Analytics.link({
          link_type: "o",
          link_name: `checkout: payment: ${customerStatus}: continue`,
        });
        continueDisable(true);
        navigate("paymentRedirect");
      },
      back: () => {
        if (applicationType == "normal") {
          Analytics.link({
            link_type: "o",
            link_name: `checkout: payment: ${customerStatus}: back to review`,
          });
        } else {
          Analytics.link({
            link_type: "o",
            link_name: `checkout: payment: ${customerStatus}: back`,
          });
        }
      },
    }));

    /**
     * Call getIntentData function
     *
     * @call page load
     */
    useEffect(() => {
      getIntentData();
    }, []);

    /**
     * Get all Payment intent data
     *
     * @usage MS API call to get all available payment types, saved credit cards, new card url, remove card url etc.
     * @usage Set the state of intentData, allCreditCards, addCreditCardUrl, paymentMethodUrl
     * @call on function call
     */
    const getIntentData = async () => {
      showSpinner();
      paymentServices
        .getPaymentIntent(postOrderResponse?.productOrderId, paymentCallbackUrl)
        .then((res) => {
          if (!res.fail && res.response?.data) {
            setIntentData(res.response.data);
            setAllCreditCards(res.response.data?.savedCards);
            setAddCreditCardUrl(
              paymentConfiguration.paymentGatewayBaseUrl +
                res.response.data?.newCardUrl
            );
            setPaymentMethodUrl(res.response.data?.paymentUrl);
            //Drupal call to get the payment option content
            getPaymentOptionsContent(res.response.data?.paymentTypes);
          } else {
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${
                res?.error?.data?.result?.message
                  ? res.error.data.result.message
                  : res?.error?.data?.message
                  ? res.error.data.message
                  : ""
              }`,
              page_errors_code: `chk-dxl-g-id-${res?.error?.status}`,
            });
            //Drupal call to get the payment option content
            getPaymentOptionsContent(defaultPaymentTypes);
          }
        })
        .catch((err) => {
          if (err) {
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${err.message ? err.message : ""}`,
              page_errors_code: `chk-dxl-g-id-${err?.response?.status}`,
            });
          }
          //Drupal call to get the payment option content
          getPaymentOptionsContent(defaultPaymentTypes);
        });
    };

    /**
     * Get all payment options
     *
     * @usage Drupal API call to get payment options and add credit card block content
     * @usage Filter all payment options(drupal) with available payment types(MS)
     * @usage Set the state of paymentOptions and addCreditCard
     * @call page load
     */

    const getPaymentOptionsContent = async (paymentTypes) => {
      showSpinner();
      paymentServices
        .getPaymentOptionsData()
        .then((results) => {
          const allPaymentOptions = results?.sections[0];
          const allowedMethods = (paymentTypes || []).map((m) => m?.name);
          const filteredMethods = (allPaymentOptions?.faqs || []).filter(
            (o) => allowedMethods.indexOf(o?.quesId) !== -1
          );

          setPaymentOptions(filteredMethods);
          setAddCreditCard(results?.sections[1]?.faqs[0]);

          if (results && results.status === "error") {
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${results.reason ? results.reason : ""}`,
              page_errors_code: `chk-d-g-po`,
            });
          }
          hideSpinner();
        })
        .catch((err) => {
          if (err) {
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${err.message ? err.message : ""}`,
              page_errors_code: `chk-d-g-po-${err?.response?.status}`,
            });
          }
          hideSpinner();
        });
    };

    /**
     * Get view Analytics
     *
     * @usage Get the required view analytics of the page
     * @call on function call
     */
    function getViewAnalytics() {
      if (applicationType == "normal") {
        return {
          page_event: "view",
          page_name: `checkout: payment: ${customerStatus}`,
        };
      } else {
        if (currentJourneyType == journeyType.upgrade) {
          return {
            page_event: "view",
            page_name: `checkout: payment: ${customerStatus}`,
            delivery_type:
              deliveryDetails?.receiveType == "store" ? "collect" : "deliver",
            authentication_id_type:
              authDetailsData?.idType == "PASSPORT" ? "passport" : "id",
            upgrade_eligibility_status: upgradeResponse?.eligibilityStatus,
            upgrade_eligible_date: upgradeResponse?.upgradeDate || "",
            upgrade_customer_type: upgradeResponse?.c3dCustomer
              ? "C3D"
              : upgradeResponse?.business
              ? "CBU"
              : "OTHER",
          };
        } else {
          return {
            page_event: "view",
            page_name: `checkout: payment: ${customerStatus}`,
            delivery_type:
              deliveryDetails?.receiveType == "store" ? "collect" : "deliver",
            authentication_id_type:
              authDetailsData?.idType == "PASSPORT" ? "passport" : "id",
          };
        }
      }
    }

    /**
     * Page Analytics
     *
     * @usage Excecute page view analytics
     * @call page load
     */
    useEffect(() => {
      if (!viewAnalytics && customerStatus) {
        const analytics = getViewAnalytics();
        analytics.multicart_flag = cartDetails?.isMulti ? true : false;

        Analytics.view({
          ...analytics,
          ...Analytics.getProfileData(userLoggedIn, profileData, authDetailsData),
        });

        setViewAnalytics(true);
      }
    }, [customerStatus]);

    /**
     * Continue button enable/disable
     *
     * @usage Enable the continue button on selecting any saved credit card
     * @call on credit card selection
     */
    useEffect(() => {
      if (selectedCreditCard) {
        continueDisable(false);
      }
    }, [selectedCreditCard]);

    useEffect(() => {
      if (showSnackBar) {
        let timer = setTimeout(() => closeSnackBar(), 3000);
        return () => {
          clearTimeout(timer);
        };
      }
    }, [showSnackBar]);

    /**
     * Add key/value for payment options object
     *
     * @usage Add "id,name" key/value to an existing object for mapping
     * @call on function call
     */
    const mapQuestionIdObjectKey = (item) => {
      if (item.quesId) {
        item.id = item.quesId;
      }

      if (item.title) {
        item.name = item.title;
      }

      return item;
    };

    /**
     * Get all credit cards
     *
     * @usage MS API call to get all the saved credit card
     * @usage Set the state of allCreditCards
     * @call on function call
     */
    const getAllCreditCardData = async () => {
      paymentServices
        .getPaymentIntent(postOrderResponse?.productOrderId, paymentCallbackUrl)
        .then((res) => {
          if (!res.fail && res.response?.data) {
            if (res.response.data?.savedCards?.length) {
              setAllCreditCards(res.response.data.savedCards);
            } else {
              setAllCreditCards([]);
              //Show warning message
              setShowWarningMsg(true);
              continueDisable(false);
            }
          } else {
            setAllCreditCards([]);
            //Show warning message
            setShowWarningMsg(true);
            continueDisable(false);
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${
                res?.error?.data?.result?.message
                  ? res.error.data.result.message
                  : res?.error?.data?.message
                  ? res.error.data.message
                  : ""
              }`,
              page_errors_code: `chk-dxl-g-sc-${res?.error?.status}`,
            });
          }
          hideSpinner();
          displaySnackBar(labels.payment.removeCreditCard);
        })
        .catch((err) => {
          if (err) {
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${err.message ? err.message : ""}`,
              page_errors_code: `chk-dxl-g-sc-${err?.response?.status}`,
            });
          }
          setAllCreditCards([]);
          //Show warning message
          setShowWarningMsg(true);
          continueDisable(false);
          hideSpinner();
          displaySnackBar(labels.payment.removeCreditCard);
        });
    };

    /**
     * Set selected payment option
     *
     * @usage Set the state of selectedPaymentOption
     * @usage Enable continue button for guest user
     * @call on click call
     */
    const paymentMethodClickHandler = async (data) => {
      setSelectedPaymentOption(data.quesId);

      Analytics.link({
        ...Analytics.getUtagData([
          "page_name",
          "page_channel",
          "page_master_tab",
          "visitor_login_status",
          "visitor_customer_id",
          "visitor_id_hash",
          "visitor_msisdn_hash",
        ]),
        visitor_subscription_type:
          applicationType == "normal"
            ? analyticsData.visitor_subscription_type
            : "postpaid",
        link_type: "o",
        page_tool_event: "use",
        page_tool_name: `checkout: payment: ${customerStatus}: select payment`,
        filter_query: data.quesId,
      });

      if (data.quesId == visaLabel) {
        setShowAllCreditCards(true);
        setPaymentMethodUrl(intentData?.paymentUrl);
        if (allCreditCards.length == 0) {
          setShowWarningMsg(true);
          continueDisable(false);
        } else {
          setShowWarningMsg(false);
          continueDisable(true);
        }
      } else {
        await updatePaymentMethodUrl(data.quesId);
        setShowAllCreditCards(false);
        continueDisable(false);
      }
    };

    /**
     * Update seleted payment method URL
     *
     * @usage Filter payment type from intent data by selected payment method id
     * @usage Set the state of paymentMethodUrl
     * @call on click call
     */
    const updatePaymentMethodUrl = (id) => {
      const filteredMethod = (intentData?.paymentTypes || []).filter(
        (o) => o.name == id
      );

      setPaymentMethodUrl(filteredMethod[0]?.paymentUrl);
    };

    /**
     * Change payment option
     *
     * @usage Reset the state of selectedPaymentOption, paymentMethodUrl, selectedCreditCard,
     * showWarningMsg, showAllCreditCards
     * @call on click call
     */
    const changePaymentMethod = () => {
      setSelectedPaymentOption(null);
      setPaymentMethodUrl(intentData?.paymentUrl); //Default value
      setSelectedCreditCard(null);
      setShowAllCreditCards(false);
      setShowWarningMsg(false);
      continueDisable(true);
    };

    /**
     * Set selected credit card
     *
     * @usage Set the state of selectedCreditCard and paymentMethodUrl
     * @call on click call
     */
    const creditCardClickHandler = (type, data) => {
      setSelectedCreditCard(data.id);

      if (type == "add") {
        setPaymentMethodUrl(addCreditCardUrl);
      } else {
        setPaymentMethodUrl(data?.href);
      }
    };

    /**
     * Display confirm modal
     *
     * @usage Show confirm modal on click of delete button from saved credit card
     * @usage Set the state of delete id of the credit card
     * @call on click call
     */
    const displyConfirmModal = (data) => {
      setDeleteId(data.id);
      setShowConfirmModal(true);

      Analytics.link({
        ...Analytics.getUtagData([
          "page_name",
          "page_channel",
          "page_master_tab",
          "visitor_login_status",
          "visitor_customer_id",
          "visitor_id_hash",
          "visitor_msisdn_hash",
        ]),
        visitor_subscription_type:
          applicationType == "normal"
            ? analyticsData.visitor_subscription_type
            : "postpaid",
        page_form_event: "start",
        page_form_name: `checkout: payment: ${customerStatus}: delete card`,
      });
    };

    /**
     * Cancel confirm modal
     *
     * @usage Cancel confirm modal on click of cancel button
     * @call on click call
     */
    const cancelConfirmModel = () => {
      setDeleteId(null);
      setShowConfirmModal(false);
    };

    /**
     * Approve confirm modal
     *
     * @usage Approve confirm modal on click of submit button
     * @usage Call MS API to delete credit card
     * @call on click call
     */
    const approveConfirmModel = async () => {
      showSpinner();
      paymentServices
        .removeStoredCard(deleteId)
        .then((res) => {
          if (!res.fail && res.response?.data?.responseCode == "SUCCESS") {
            setDeleteId(null);
            setSelectedCreditCard(null);
            setPaymentMethodUrl(intentData?.paymentUrl);
            getAllCreditCardData();
          } else {
            displaySnackBar(labels.payment.removeCreditCardFail);
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${
                res?.error?.data?.result?.message
                  ? res.error.data.result.message
                  : res?.error?.data?.message
                  ? res.error.data.message
                  : ""
              }`,
              page_errors_code: `chk-dxl-p-rc-${res?.error?.status}`,
            });
            hideSpinner();
          }
        })
        .catch((err) => {
          if (err) {
            Analytics.link({
              ...Analytics.getUtagData(["page_name"]),
              page_form_event: "error",
              page_errors: `${err.message ? err.message : ""}`,
              page_errors_code: `chk-dxl-p-rc-${err?.response?.status}`,
            });
          }
          hideSpinner();
        });
      setShowConfirmModal(false);
    };

    const displaySnackBar = (msg) => {
      setSnackBarMsg(msg);
      setShowSnackBar(true);
    };

    const closeSnackBar = () => {
      setSnackBarMsg(null);
      setShowSnackBar(false);
    };

    return (
      <>
        <CustomContainer className={styles["payment-container"]}>
          <CustomContainer className={styles["options-section"]}>
            <FormHeading
              title="Select payment method"
              desc="How would you like to pay today?"
              id={`checkout-payment-options-section-heading`}
              className={styles["top-wrapper"]}
              headingClassName={styles["top-wrapper__heading"]}
            />
            {paymentOptions?.length > 0 && (
              <>
                <CustomContainer className={styles["tiles-section"]}>
                  {(!showAllCreditCards || !selectedPaymentOption) &&
                    paymentOptions.map((item, index) => (
                      <PaymentMethodTile
                        variant="option"
                        data={mapQuestionIdObjectKey(item)}
                        key={index}
                        handleOnClick={paymentMethodClickHandler}
                        selectedItem={selectedPaymentOption}
                        id={`checkout-payment-option-tile-${index}`}
                      />
                    ))}

                  {showAllCreditCards &&
                    paymentOptions
                      .filter((item) => item.quesId == selectedPaymentOption)
                      .map((item, index) => (
                        <PaymentMethodTile
                          variant="option"
                          data={mapQuestionIdObjectKey(item)}
                          key={index}
                          handleOnClick={paymentMethodClickHandler}
                          selectedItem={selectedPaymentOption}
                          id={`checkout-payment-option-tile-${index}`}
                          showDesc={false}
                        />
                      ))}
                </CustomContainer>

                {selectedPaymentOption && showAllCreditCards && (
                  <CustomContainer
                    className={styles["change-payment-label"]}
                    onClick={changePaymentMethod}
                    id={`checkout-payment-option-change`}
                  >
                    Change payment method
                  </CustomContainer>
                )}
              </>
            )}
          </CustomContainer>
          {selectedPaymentOption == visaLabel && showAllCreditCards && (
            <CustomContainer className={styles["all-cards-section"]}>
              <FormHeading
                title="Pay with credit / debit card"
                desc="Which card would you like to pay with?"
                id={`checkout-payment-all-cards-section-heading`}
                className={styles["top-wrapper"]}
                headingClassName={styles["top-wrapper__heading"]}
              />
              {showWarningMsg && (
                <CustomContainer className={styles["error-container"]}>
                  <CustomContainer className={styles["icon"]}>
                    <Image
                      img_path={`${imagePrefix}/assets/images/warning-black.svg`}
                    />
                  </CustomContainer>
                  <CustomContainer className={styles["text"]}>
                    {labels.payment.noCreditCardWarning}
                  </CustomContainer>
                </CustomContainer>
              )}

              {allCreditCards?.length > 0 && (
                <CustomContainer className={styles["tiles-section"]}>
                  {allCreditCards.map((item, index) => (
                    <PaymentMethodTile
                      variant="card"
                      data={item}
                      key={index}
                      defaultIcon={true}
                      defaultIconUrl={`${imagePrefix}/assets/images/icon-credit-card.svg`}
                      handleOnDelete={displyConfirmModal}
                      handleOnClick={(data) =>
                        creditCardClickHandler("all", data)
                      }
                      selectedItem={selectedCreditCard}
                      showDesc={false}
                      id={`checkout-payment-card-tile-${index}`}
                    />
                  ))}
                  <PaymentMethodTile
                    variant="card"
                    data={mapQuestionIdObjectKey(addCreditCard)}
                    handleOnClick={(data) =>
                      creditCardClickHandler("add", data)
                    }
                    selectedItem={selectedCreditCard}
                    id={`checkout-payment-add-card-tile`}
                    deleteOption={false}
                  />
                </CustomContainer>
              )}
            </CustomContainer>
          )}
        </CustomContainer>
        {showConfirmModal ? (
          <ConfirmModal
            show={showConfirmModal}
            subtext="You’re about to remove one of your saved cards from your profile. Would you like to continue?"
            handleConfirm={approveConfirmModel}
            handleCancel={cancelConfirmModel}
            cancelLabel="Cancel"
            confirmLabel="Remove card"
          />
        ) : null}
        {showSnackBar ? (
          <SnackBar
            message={snackBarMsg}
            className={styles["snack-bar-styling"]}
            closeSnackBar={closeSnackBar}
            isVisible={showSnackBar}
          />
        ) : null}
      </>
    );
  }
);

CheckoutPayment.propTypes = {
  continueDisable: PropTypes.func,
  customerStatus: PropTypes.string,
  applicationType: PropTypes.string,
  availablePaymentMethods: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    deliveryDetails: state.creditCheckDetails.deliveryDetails,
    authDetailsData: state.checkoutDetails.authDetails,
    upgradeResponse: state.upgrade.upgradeResponse,
    creditVettingDetails: state.checkoutDetails.creditVettingResult,
    availablePaymentMethods: state.cartDetails?.available_payment_methods,
    postOrderResponse: state.checkoutDetails.postOrderResponse,
  };
};

const mapDispatchToProps = {
  setPaymentMethodUrl,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(CheckoutPayment);
